/**
 * Checkout upsell modal.
 *
 * Intercepts the cart-drawer checkout button: the first real click opens the
 * upsell <dialog> (rendered by c-checkout-upsell.liquid). Closing the dialog
 * (X, "continue", Escape, or backdrop) proceeds to checkout by re-triggering the
 * button. Quick-add inside the dialog re-renders the cart, so the checkout button
 * is re-queried live before proceeding.
 *
 * ponytail: click interception only (covers the button). Form Enter-submit is
 * rare here and falls through to normal checkout — acceptable.
 */
(() => {
  const SELECTOR = '#checkout, .cart__checkout-button';
  /** @type {ReturnType<typeof setTimeout> | null} */
  let proceedingTimer = null;
  let proceeding = false;

  const getDialog = () => document.querySelector('dialog[data-c-checkout-upsell]');
  const getLiveCheckout = () =>
    document.querySelector('cart-drawer-component #checkout') ||
    document.querySelector('cart-drawer-component .cart__checkout-button');

  const markProceeding = () => {
    proceeding = true;
    if (proceedingTimer) clearTimeout(proceedingTimer);
    // Survive click-through when a <dialog> closes mid-gesture (the pointer
    // event retargets to the checkout button sitting under the modal).
    proceedingTimer = setTimeout(() => {
      proceeding = false;
      proceedingTimer = null;
    }, 400);
  };

  const submitCheckout = () => {
    const checkout = getLiveCheckout() || document.querySelector(SELECTOR);
    if (!checkout) return;

    const form = checkout.form ?? document.getElementById('cart-form');
    if (form) {
      form.requestSubmit(checkout);
      return;
    }

    checkout.click();
  };

  const closeAndProceed = (dialog, event) => {
    event.preventDefault();
    event.stopPropagation();
    markProceeding();
    dialog.close();
  };

  const bindDialog = (dialog) => {
    if (dialog.dataset.cuBound) return;
    dialog.dataset.cuBound = '1';

    // Any close proceeds to checkout (re-query: cart may have re-rendered).
    dialog.addEventListener('close', () => {
      markProceeding();
      queueMicrotask(submitCheckout);
    });

    dialog
      .querySelectorAll('[data-c-checkout-upsell-close], [data-c-checkout-upsell-continue]')
      .forEach((el) => el.addEventListener('click', (event) => closeAndProceed(dialog, event)));

    // Backdrop click closes.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeAndProceed(dialog, event);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = /** @type {Element | null} */ (event.target);
      const button = target?.closest?.(SELECTOR);
      if (!button || !button.closest('cart-drawer-component')) return;

      // After the upsell closes: let checkout through (including click-through).
      if (proceeding) return;

      const dialog = getDialog();
      if (!dialog || dialog.open) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      bindDialog(dialog);
      dialog.showModal();
    },
    true
  );
})();
